import { useEffect, useMemo, useState } from "react";
import { Pencil, Plus, Trash2, X, Search, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  useCelebrationTiers,
  useAllTierAssignments,
  useCreateTier,
  useUpdateTier,
  useDeleteTier,
  useApplyTierAssignmentChanges,
  useTeamMembers,
  type CelebrationTierWithCount,
  type TeamMemberRow,
} from "@/hooks/queries";

/**
 * MS555 — Settings → Tiers panel.
 *
 * Custom tiers per Lucky's lock in MS552 §3 (named-tier-from-job-
 * architecture was rejected in MS550 in favour of buyer-defined
 * names). Tier dedupe is enforced at picker time — the buyer never
 * sees a 23505 from the DB-level UNIQUE(team_member_id) backstop on
 * team_member_tier_assignments.
 *
 * This panel is CRUD + display only. The engine integration that
 * makes tier ceilings override the company default is a separate
 * future ticket per the MS555 task brief.
 */

const MIN_TIER_CEILING_DOLLARS = 0;
// Schema CHECK is per_celebration_ceiling_cents >= 0; companies.budget
// CHECK in MS552 was 0–100,000 cents but the new tier table uses just
// >= 0. We cap UI input at $100,000 as a sanity bound — anything
// higher is almost certainly a buyer typing mistake.
const MAX_TIER_CEILING_DOLLARS = 100_000;

function dollarsToCents(dollars: number): number {
  // round to nearest cent to avoid floating-point drift on values like
  // "$120.45" → 12044.999999… → 12044 instead of 12045.
  return Math.round(dollars * 100);
}

function centsToDollars(cents: number): number {
  return cents / 100;
}

function formatAud(cents: number): string {
  const dollars = centsToDollars(cents);
  // No decimals when the value is whole-dollar — matches the wireframe's
  // "$200 per gift". Two decimals otherwise so $99.50 doesn't render as
  // a misleading "$99".
  if (Number.isInteger(dollars)) {
    return `$${dollars.toLocaleString("en-AU")}`;
  }
  return `$${dollars.toLocaleString("en-AU", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

interface TiersPanelProps {
  companyId: string | null | undefined;
}

export function TiersPanel({ companyId }: TiersPanelProps) {
  const tiersQuery = useCelebrationTiers(companyId);
  const [editingTier, setEditingTier] = useState<CelebrationTierWithCount | null>(null);
  const [creating, setCreating] = useState(false);
  // Two-step destructive flow: clicking Delete on a card opens this
  // confirm dialog. We deliberately don't require typing the tier name
  // for v1 — the MS552 §3 wireframe shows it as a step 2 enhancement,
  // and the cascade is recoverable by re-creating + re-assigning.
  const [pendingDelete, setPendingDelete] = useState<CelebrationTierWithCount | null>(null);
  const deleteTier = useDeleteTier(companyId);

  const tiers = tiersQuery.data ?? [];
  const hasTiers = tiers.length > 0;

  if (tiersQuery.isLoading) {
    return (
      <div className="text-sm text-charcoal/55">Loading tiers…</div>
    );
  }
  if (tiersQuery.isError) {
    return (
      <div className="text-sm text-coral">
        Couldn't load tiers. Refresh to try again.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {!hasTiers ? (
        <div className="rounded-lg border border-dashed border-charcoal/20 p-6 text-center">
          <p
            className="text-sm text-charcoal/80"
            style={{ fontFamily: "DM Sans, sans-serif", lineHeight: 1.55 }}
          >
            Create custom tiers to give different employees different gift
            ceilings.
          </p>
          <p
            className="mt-1.5 text-xs text-charcoal/55"
            style={{ fontFamily: "DM Sans, sans-serif", lineHeight: 1.55 }}
          >
            Example: Founding team gets up to $200, everyone else uses the
            company default.
          </p>
          <Button
            variant="primary"
            size="sm"
            className="mt-4"
            onClick={() => setCreating(true)}
          >
            <Plus className="mr-1 h-4 w-4" /> Create your first tier
          </Button>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {tiers.map((tier) => (
              <TierCard
                key={tier.id}
                tier={tier}
                onEdit={() => setEditingTier(tier)}
                onDelete={() => setPendingDelete(tier)}
              />
            ))}
          </div>
          <Button variant="outline" size="sm" onClick={() => setCreating(true)}>
            <Plus className="mr-1 h-4 w-4" /> Create new tier
          </Button>
        </>
      )}

      {/* Create dialog */}
      <TierFormDialog
        key={creating ? "create" : "create-closed"}
        open={creating}
        onOpenChange={setCreating}
        companyId={companyId}
        mode="create"
      />
      {/* Edit dialog — keyed on tier id so a fresh-mount form resets
          drafts when the buyer switches between tiers without closing. */}
      <TierFormDialog
        key={editingTier?.id ?? "edit-closed"}
        open={Boolean(editingTier)}
        onOpenChange={(open) => {
          if (!open) setEditingTier(null);
        }}
        companyId={companyId}
        mode="edit"
        tier={editingTier ?? undefined}
      />

      <AlertDialog
        open={Boolean(pendingDelete)}
        onOpenChange={(open) => {
          if (!open) setPendingDelete(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete {pendingDelete?.name ?? "this tier"}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pendingDelete?.member_count
                ? `${pendingDelete.member_count} member${
                    pendingDelete.member_count === 1 ? "" : "s"
                  } will fall back to the company default ceiling.`
                : "This tier has no assigned members."}{" "}
              You can recreate the tier later, but assignments aren't restored.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteTier.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-coral text-white hover:bg-coral/90"
              disabled={deleteTier.isPending}
              onClick={(e) => {
                e.preventDefault();
                if (!pendingDelete) return;
                deleteTier.mutate(pendingDelete.id, {
                  onSuccess: () => {
                    toast.success(`Deleted "${pendingDelete.name}"`);
                    setPendingDelete(null);
                  },
                  onError: () => {
                    toast.error("Couldn't delete the tier");
                  },
                });
              }}
            >
              {deleteTier.isPending ? "Deleting…" : "Delete tier"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function TierCard({
  tier,
  onEdit,
  onDelete,
}: {
  tier: CelebrationTierWithCount;
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <div
      data-testid={`tier-card-${tier.id}`}
      className="rounded-lg border border-charcoal/10 p-4 flex flex-col gap-2"
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <h4
            className="text-base font-medium text-navy"
            style={{ fontFamily: "Newsreader, Georgia, serif" }}
          >
            {tier.name}
          </h4>
          <p
            className="text-sm text-charcoal/70"
            style={{ fontFamily: "DM Sans, sans-serif" }}
          >
            {formatAud(tier.per_celebration_ceiling_cents)} per gift
          </p>
        </div>
      </div>
      <p className="text-xs text-charcoal/55">
        {tier.member_count} member{tier.member_count === 1 ? "" : "s"} assigned
      </p>
      {/* Variant A (MS-audit): full-width split actions with a light
          hairline border instead of the heavy 2px-navy outline buttons.
          flex-1 + h-8 keeps both inside the card frame at every column
          width (the old no-wrap pair broke the frame below ~440px), and
          the calmer weight stops a card action from competing with the
          page's primary CTAs. Delete stays neutral until hover. */}
      <div className="mt-1 flex gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={onEdit}
          aria-label={`Edit ${tier.name}`}
          className="h-8 flex-1 border border-charcoal/15 text-navy hover:bg-charcoal/[0.04] hover:text-navy"
        >
          <Pencil className="mr-1.5 h-3.5 w-3.5" /> Edit
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={onDelete}
          aria-label={`Delete ${tier.name}`}
          className="h-8 flex-1 border border-charcoal/15 text-charcoal/60 hover:border-coral/30 hover:bg-coral/[0.06] hover:text-coral"
        >
          <Trash2 className="mr-1.5 h-3.5 w-3.5" /> Delete
        </Button>
      </div>
    </div>
  );
}

/**
 * Create + edit dialog for a tier. Driven by `mode` so the heading,
 * primary button, and submit handler swap correctly. In edit mode we
 * pre-seed the name, ceiling, and the picker's selected set with the
 * tier's current members.
 */
interface TierFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  companyId: string | null | undefined;
  mode: "create" | "edit";
  tier?: CelebrationTierWithCount;
}

interface PickerStagedMember {
  teamMemberId: string;
  /** "add" = unassigned member being added. "move" = currently in another tier. */
  kind: "add" | "move" | "existing";
  /** Present when kind = "move": the assignment id to DELETE on save. */
  previousAssignmentId?: string;
  /** Present when kind = "move": the previous tier name for the chip badge. */
  previousTierName?: string;
  /** Present when kind = "existing": the assignment id in *this* tier. */
  existingAssignmentId?: string;
}

interface PickerPendingConflict {
  teamMember: TeamMemberRow;
  previousAssignmentId: string;
  previousTierName: string;
}

function TierFormDialog({
  open,
  onOpenChange,
  companyId,
  mode,
  tier,
}: TierFormDialogProps) {
  const teamMembersQuery = useTeamMembers(companyId);
  const allAssignmentsQuery = useAllTierAssignments(companyId);
  const createTier = useCreateTier(companyId);
  const updateTier = useUpdateTier(companyId);
  const applyChanges = useApplyTierAssignmentChanges(companyId);

  const [name, setName] = useState<string>(tier?.name ?? "");
  const [ceilingDraft, setCeilingDraft] = useState<string>(
    tier ? centsToDollars(tier.per_celebration_ceiling_cents).toString() : "",
  );
  const [search, setSearch] = useState("");
  const [staged, setStaged] = useState<PickerStagedMember[]>([]);
  const [pendingConflict, setPendingConflict] =
    useState<PickerPendingConflict | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Pre-seed the picker with the tier's existing members on edit-open.
  // The useEffect resets when the dialog opens so closing-then-reopening
  // without unmounting (e.g. via the parent's `key` swap) still works.
  useEffect(() => {
    if (!open) return;
    if (mode !== "edit" || !tier) {
      setStaged([]);
      return;
    }
    const all = allAssignmentsQuery.data ?? [];
    const seedExisting: PickerStagedMember[] = all
      .filter((a) => a.tier_id === tier.id)
      .map((a) => ({
        teamMemberId: a.team_member_id,
        kind: "existing" as const,
        existingAssignmentId: a.id,
      }));
    setStaged(seedExisting);
  }, [open, mode, tier, allAssignmentsQuery.data]);

  // Memoize the nullable-fallback shape so the downstream useMemo
  // hooks don't see a new array identity on every render — the
  // exhaustive-deps lint warns about this for good reason. The hooks
  // below treat allTeamMembers/allAssignments as stable references.
  const allTeamMembers = useMemo(
    () => teamMembersQuery.data ?? [],
    [teamMembersQuery.data],
  );
  const allAssignments = useMemo(
    () => allAssignmentsQuery.data ?? [],
    [allAssignmentsQuery.data],
  );

  // teamMemberId → assignment row in some other tier, for the dedupe
  // affordance. Building this map at modal open time is the MS552 §3
  // "preflight" mentioned in the brief — every search result lookup
  // is O(1) without an additional round-trip.
  const assignmentByMemberId = useMemo(() => {
    const map = new Map<
      string,
      { assignmentId: string; tierId: string; tierName: string }
    >();
    for (const a of allAssignments) {
      map.set(a.team_member_id, {
        assignmentId: a.id,
        tierId: a.tier_id,
        tierName: a.tier_name,
      });
    }
    return map;
  }, [allAssignments]);

  const stagedById = useMemo(() => {
    const set = new Set<string>();
    for (const s of staged) set.add(s.teamMemberId);
    return set;
  }, [staged]);

  const filteredMembers = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return [] as TeamMemberRow[];
    return allTeamMembers
      .filter(
        (m) =>
          m.name.toLowerCase().includes(q) ||
          m.email.toLowerCase().includes(q),
      )
      .slice(0, 8);
  }, [search, allTeamMembers]);

  const stagedMembers = useMemo(() => {
    const byId = new Map(allTeamMembers.map((m) => [m.id, m]));
    return staged
      .map((s) => {
        const member = byId.get(s.teamMemberId);
        return member ? { staged: s, member } : null;
      })
      .filter((x): x is { staged: PickerStagedMember; member: TeamMemberRow } => Boolean(x));
  }, [staged, allTeamMembers]);

  const handleSelectMember = (member: TeamMemberRow) => {
    setSearch("");
    setError(null);
    if (stagedById.has(member.id)) return; // already in this tier; no-op.
    const existing = assignmentByMemberId.get(member.id);
    // Edit mode: a member already in *this* tier shouldn't appear in
    // the picker because they're already staged as `existing`. We only
    // care about conflicts with *other* tiers.
    if (existing && existing.tierId !== tier?.id) {
      setPendingConflict({
        teamMember: member,
        previousAssignmentId: existing.assignmentId,
        previousTierName: existing.tierName,
      });
      return;
    }
    setStaged((prev) => [
      ...prev,
      { teamMemberId: member.id, kind: "add" },
    ]);
  };

  const handleConfirmMove = () => {
    if (!pendingConflict) return;
    setStaged((prev) => [
      ...prev,
      {
        teamMemberId: pendingConflict.teamMember.id,
        kind: "move",
        previousAssignmentId: pendingConflict.previousAssignmentId,
        previousTierName: pendingConflict.previousTierName,
      },
    ]);
    setPendingConflict(null);
  };

  const handleRemoveChip = (teamMemberId: string) => {
    setStaged((prev) => prev.filter((s) => s.teamMemberId !== teamMemberId));
  };

  const ceilingDollars = Number.parseFloat(ceilingDraft);
  const ceilingIsValid =
    !Number.isNaN(ceilingDollars) &&
    ceilingDollars >= MIN_TIER_CEILING_DOLLARS &&
    ceilingDollars <= MAX_TIER_CEILING_DOLLARS;
  const nameIsValid = name.trim().length > 0 && name.trim().length <= 80;
  const canSubmit = nameIsValid && ceilingIsValid && !submitting;

  const handleSubmit = async () => {
    if (!companyId || !canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const trimmedName = name.trim();
      const ceilingCents = dollarsToCents(ceilingDollars);

      let tierId: string;
      if (mode === "create") {
        const newTier = await createTier.mutateAsync({
          companyId,
          name: trimmedName,
          perCelebrationCeilingCents: ceilingCents,
        });
        tierId = newTier.id;
      } else {
        if (!tier) throw new Error("Tier missing in edit mode");
        await updateTier.mutateAsync({
          tierId: tier.id,
          name: trimmedName,
          perCelebrationCeilingCents: ceilingCents,
        });
        tierId = tier.id;
      }

      // Compute the assignment delta. `existing` rows in edit mode that
      // got chipped out before save are the `removes`. `add` and `move`
      // rows go into adds + moves respectively. Members the buyer never
      // touched stay where they are.
      const removes: string[] = [];
      const adds: string[] = [];
      const moves: { teamMemberId: string; previousAssignmentId: string }[] = [];

      if (mode === "edit" && tier) {
        const stillStagedIds = new Set(staged.map((s) => s.teamMemberId));
        const existingForThisTier = allAssignments.filter(
          (a) => a.tier_id === tier.id,
        );
        for (const a of existingForThisTier) {
          if (!stillStagedIds.has(a.team_member_id)) {
            removes.push(a.id);
          }
        }
      }
      for (const s of staged) {
        if (s.kind === "add") adds.push(s.teamMemberId);
        else if (s.kind === "move" && s.previousAssignmentId) {
          moves.push({
            teamMemberId: s.teamMemberId,
            previousAssignmentId: s.previousAssignmentId,
          });
        }
        // `existing` rows don't need an op — they're already in this tier.
      }

      if (removes.length || adds.length || moves.length) {
        await applyChanges.mutateAsync({
          tierId,
          removes,
          adds,
          moves,
        });
      }

      toast.success(mode === "create" ? `Created "${trimmedName}"` : `Updated "${trimmedName}"`);
      onOpenChange(false);
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Couldn't save the tier";
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {mode === "create" ? "Create tier" : `Edit ${tier?.name ?? "tier"}`}
            </DialogTitle>
            <DialogDescription>
              {mode === "create"
                ? "Give a group of employees a different gift ceiling than your company default."
                : "Rename, adjust the ceiling, or change members."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5 mt-2">
            <div>
              <Label className="text-navy font-medium" htmlFor="tier-name-input">
                Name
              </Label>
              <Input
                id="tier-name-input"
                className="mt-1"
                placeholder="e.g. Founding team"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={80}
              />
              <p className="mt-1 text-xs text-charcoal/55">
                Buyers see this label on every celebration card.
              </p>
            </div>

            <div>
              <Label
                className="text-navy font-medium"
                htmlFor="tier-ceiling-input"
              >
                Per-celebration ceiling
              </Label>
              <div className="mt-1 flex items-center gap-2">
                <span className="text-sm text-charcoal/70">AUD $</span>
                <Input
                  id="tier-ceiling-input"
                  className="w-40"
                  type="number"
                  min={MIN_TIER_CEILING_DOLLARS}
                  max={MAX_TIER_CEILING_DOLLARS}
                  step="1"
                  inputMode="decimal"
                  value={ceilingDraft}
                  onChange={(e) => setCeilingDraft(e.target.value)}
                  placeholder="200"
                />
              </div>
              <p className="mt-1 text-xs text-charcoal/55">
                Per gift, in Australian dollars. Overrides the company default
                for assigned employees.
              </p>
            </div>

            <div>
              <Label className="text-navy font-medium" htmlFor="member-picker-search">
                Members in this tier
              </Label>
              <div className="mt-1 relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-charcoal/40" />
                <Input
                  id="member-picker-search"
                  className="pl-8"
                  placeholder="Search team members…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  aria-label="Search team members"
                />
              </div>
              {search.trim().length > 0 && filteredMembers.length > 0 ? (
                <div className="mt-1 border border-charcoal/10 rounded-lg bg-white shadow-sm divide-y divide-charcoal/5 max-h-64 overflow-y-auto">
                  {filteredMembers.map((member) => {
                    const conflict = assignmentByMemberId.get(member.id);
                    const isInThisTier =
                      conflict && conflict.tierId === tier?.id;
                    const alreadyStaged = stagedById.has(member.id);
                    return (
                      <button
                        key={member.id}
                        type="button"
                        disabled={alreadyStaged || isInThisTier}
                        onClick={() => handleSelectMember(member)}
                        className="w-full text-left px-3 py-2 hover:bg-cream/60 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-between gap-3"
                        aria-label={`Add ${member.name} to tier`}
                      >
                        <span className="min-w-0">
                          <span className="block text-sm text-navy font-medium truncate">
                            {member.name}
                          </span>
                          <span className="block text-xs text-charcoal/55 truncate">
                            {member.email}
                          </span>
                        </span>
                        {conflict ? (
                          <span
                            className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${
                              isInThisTier
                                ? "bg-charcoal/10 text-charcoal/60"
                                : "bg-coral/10 text-coral"
                            }`}
                          >
                            {isInThisTier ? "Already in" : `In ${conflict.tierName}`}
                          </span>
                        ) : null}
                      </button>
                    );
                  })}
                </div>
              ) : null}
              {search.trim().length > 0 && filteredMembers.length === 0 ? (
                <p className="mt-2 text-xs text-charcoal/55">
                  No matching team members.
                </p>
              ) : null}

              {stagedMembers.length > 0 ? (
                <div
                  data-testid="staged-member-chips"
                  className="mt-3 flex flex-wrap gap-1.5"
                >
                  {stagedMembers.map(({ staged: s, member }) => (
                    <span
                      key={member.id}
                      className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${
                        s.kind === "move"
                          ? "bg-coral/10 text-coral"
                          : "bg-navy/5 text-navy"
                      }`}
                    >
                      {member.name}
                      {s.kind === "move" && s.previousTierName ? (
                        <span className="text-[10px] opacity-70">
                          (moved from {s.previousTierName})
                        </span>
                      ) : null}
                      <button
                        type="button"
                        onClick={() => handleRemoveChip(member.id)}
                        aria-label={`Remove ${member.name}`}
                        className="ml-0.5 opacity-60 hover:opacity-100"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              ) : (
                <p className="mt-3 text-xs text-charcoal/55">
                  No members assigned yet. Search above to add.
                </p>
              )}
            </div>
            {error ? (
              <p className="text-sm text-coral" role="alert">{error}</p>
            ) : null}
          </div>

          <DialogFooter className="mt-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={() => void handleSubmit()}
              disabled={!canSubmit}
            >
              {submitting
                ? "Saving…"
                : mode === "create"
                  ? "Create tier"
                  : "Save changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Inline dedupe affordance per MS552 §3 — fires when the buyer
          selects a member already assigned to a different tier. */}
      <AlertDialog
        open={Boolean(pendingConflict)}
        onOpenChange={(o) => {
          if (!o) setPendingConflict(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Move {pendingConflict?.teamMember.name} from{" "}
              {pendingConflict?.previousTierName}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pendingConflict?.teamMember.name} is currently in{" "}
              <strong>{pendingConflict?.previousTierName}</strong>. Moving them
              into this tier will remove their old assignment when you save.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Skip</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleConfirmMove();
              }}
            >
              <ArrowRight className="mr-1 h-4 w-4" /> Move
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
